import React, { useState } from "react";
import axios, { API } from "../../api";

/**
 * Phase 25.1 — Aura Pro waitlist modal ("Coming Soon").
 *
 * Builds anticipation for the professional tier (Aura Assistant, Aura 911,
 * Aura Field, business/enterprise versions) BEFORE they launch — so when we
 * ship Pro, we already have an opt-in mailing list of people who explicitly
 * raised their hand.
 *
 * UX:
 *   - Tease the features in plain English (no marketing buzzwords)
 *   - Single "Notify me when Pro launches" button — captures user_id +
 *     pro_interest=true on the user record
 *   - Optional: extra "Tell me which interests you most" multi-select for
 *     better segmentation when we DO launch
 *   - Idempotent — re-opening shows a "you're on the list" state
 */
export function ProTeaserModal({ onClose, alreadySignedUp = false }) {
  const [signedUp, setSignedUp] = useState(Boolean(alreadySignedUp));
  const [submitting, setSubmitting] = useState(false);
  const [interests, setInterests] = useState({
    assistant: false,
    nine_one_one: false,
    business: false,
    family: false,
  });

  const toggleInterest = (key) =>
    setInterests((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleSignUp = async () => {
    setSubmitting(true);
    try {
      const chosen = Object.entries(interests)
        .filter(([, v]) => v)
        .map(([k]) => k);
      await axios.post(`${API}/pro/interest`, {
        interests: chosen,
      });
      setSignedUp(true);
    } catch (e) {
      console.error("pro interest signup", e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="pro-teaser-overlay">
      <div className="pro-teaser-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pro-teaser-header">
          <div className="pro-teaser-badge">👤 EMPLOYEE MODE</div>
          <button className="journal-close" onClick={onClose} data-testid="pro-teaser-close">
            ×
          </button>
        </div>

        <h2 className="pro-teaser-title">
          You can put Aura to work.
        </h2>
        <p className="pro-teaser-lede">
          Employee Mode turns Aura into something you don&apos;t just talk to — something you
          assign tasks to. First responders, business owners, caregivers, families.
          Toggle on the ones you&apos;d use, and we&apos;ll save you a seat for early access.
        </p>

        <div className="pro-teaser-features">
          <FeatureCard
            icon="🛟"
            title="Aura 911"
            body="A self-contained 911 operator system that shaves minutes off emergency calls. Pilot launching with one progressive city in 2026."
            active={interests.nine_one_one}
            onClick={() => toggleInterest("nine_one_one")}
            testid="pro-feature-911"
            disabled={signedUp}
          />
          <FeatureCard
            icon="💼"
            title="Aura Assistant"
            body="Your personal executive assistant. Reads, writes, organizes, schedules — talks like Aura, runs on your own private server, never sells your data."
            active={interests.assistant}
            onClick={() => toggleInterest("assistant")}
            testid="pro-feature-assistant"
            disabled={signedUp}
          />
          <FeatureCard
            icon="🏢"
            title="Aura for Business"
            body="The same Aura, but for small teams. Shared memory, shared calendars, shared culture. HIPAA/SOC 2 path."
            active={interests.business}
            onClick={() => toggleInterest("business")}
            testid="pro-feature-business"
            disabled={signedUp}
          />
          <FeatureCard
            icon="👨‍👩‍👧"
            title="Aura for Family"
            body="One Aura, multiple family members, voice recognition for each. Built around busy households."
            active={interests.family}
            onClick={() => toggleInterest("family")}
            testid="pro-feature-family"
            disabled={signedUp}
          />
        </div>

        {!signedUp ? (
          <>
            <button
              className="pro-teaser-cta"
              onClick={handleSignUp}
              disabled={submitting}
              data-testid="pro-teaser-signup-btn"
            >
              {submitting ? "Saving…" : "Save my seat — turn Employee Mode on at launch →"}
            </button>
            <p className="pro-teaser-fineprint">
              One email at launch. We won&apos;t share your address with anyone, ever.
            </p>
          </>
        ) : (
          <div className="pro-teaser-thanks" data-testid="pro-teaser-thanks">
            <h3>Seat saved. 💜</h3>
            <p>
              You&apos;re on the Employee Mode early list. You&apos;ll be the first to know
              when it switches on. Until then — just keep being you.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, body, active, onClick, testid, disabled }) {
  return (
    <button
      type="button"
      className={`pro-feature-card${active ? " active" : ""}`}
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
    >
      <div className="pro-feature-icon">{icon}</div>
      <div className="pro-feature-text">
        <strong>{title}</strong>
        <span>{body}</span>
      </div>
      {active && <div className="pro-feature-check">✓</div>}
    </button>
  );
}

export default ProTeaserModal;
